import { useState, useEffect, useCallback, useRef } from "react";
import * as TagsService from "../services/TagServices";

export const useTags = (initialFilters = {}) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  // Ref para armazenar os filtros atuais e evitar dependências circulares
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Função para buscar tags sem dependências circulares
  const fetchTags = useCallback(async (filtersToUse = null) => {
    setLoading(true);
    setError(null);

    try {
      const currentFilters = filtersToUse || filtersRef.current;
      const response = await TagsService.getTags(currentFilters);

      // Se a resposta tem paginação
      if (response.results) {
        setTags(response.results);
        setPagination({
          currentPage: 1,
          totalPages: Math.ceil(
            response.count / (currentFilters.page_size || 20)
          ),
          totalItems: response.count,
          itemsPerPage: currentFilters.page_size || 20,
        });
      } else {
        // Resposta simples sem paginação
        setTags(response);
      }
    } catch (err) {
      setError(err.message || "Erro ao carregar tags");
      console.error("Erro ao buscar tags:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Criar nova tag
  const createTag = useCallback(async (tagData) => {
    try {
      const newTag = await TagsService.createTag(tagData);
      setTags((prevTags) => [newTag, ...prevTags]);
      return newTag;
    } catch (err) {
      setError(err.message || "Erro ao criar tag");
      throw err;
    }
  }, []);

  // Atualizar tag
  const updateTag = useCallback(async (tagId, tagData) => {
    try {
      const updatedTag = await TagsService.updateTag(tagId, tagData);
      setTags((prevTags) =>
        prevTags.map((tag) => (tag.id === tagId ? updatedTag : tag))
      );
      return updatedTag;
    } catch (err) {
      setError(err.message || "Erro ao atualizar tag");
      throw err;
    }
  }, []);

  // Excluir tag
  const deleteTag = useCallback(async (tagId) => {
    try {
      await TagsService.deleteTag(tagId);
      setTags((prevTags) => prevTags.filter((tag) => tag.id !== tagId));
    } catch (err) {
      setError(err.message || "Erro ao excluir tag");
      throw err;
    }
  }, []);

  // Alternar compartilhamento
  const toggleShare = useCallback(async (tagId) => {
    try {
      const response = await TagsService.toggleShare(tagId);
      setTags((prevTags) =>
        prevTags.map((tag) =>
          tag.id === tagId ? { ...tag, is_shared: response.is_shared } : tag
        )
      );
      return response;
    } catch (err) {
      setError(err.message || "Erro ao alterar compartilhamento");
      throw err;
    }
  }, []);

  // Buscar tags com debounce
  const searchTags = useCallback(async (query, category = null) => {
    if (!query.trim()) return [];

    try {
      const response = await TagsService.searchTags(query, category, 10);
      return response.results || response;
    } catch (err) {
      console.error("Erro ao buscar tags:", err);
      return [];
    }
  }, []);

  // Aplicar filtros sem dependências circulares
  const applyFilters = useCallback(
    (newFilters) => {
      const updatedFilters = { ...filtersRef.current, ...newFilters };
      setFilters(updatedFilters);
      fetchTags(updatedFilters);
    },
    [fetchTags]
  );

  // Limpar filtros
  const clearFilters = useCallback(() => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    fetchTags(clearedFilters);
  }, [fetchTags]);

  // Recarregar tags
  const refreshTags = useCallback(() => {
    fetchTags(filtersRef.current);
  }, [fetchTags]);

  return {
    // Estado
    tags,
    loading,
    error,
    filters,
    pagination,

    // Ações
    createTag,
    updateTag,
    deleteTag,
    toggleShare,
    searchTags,

    // Filtros
    applyFilters,
    clearFilters,
    refreshTags,

    // Utilitários
    setError,
  };
};
